import type { RulesConfig } from '@smart-link/shared';
import {
  PLATFORMS,
  TEMPLATES,
  diffRulesOverrides,
  mergeRulesOverrides,
  templateById,
} from '@smart-link/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link as RouterLink, createFileRoute, redirect } from '@tanstack/react-router';
import { ArrowLeft, ChevronRight } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { BillingUnitSelect } from '../components/BillingUnitSelect';
import {
  LogoField,
  appNameLabel,
  appNamePlaceholder,
  logoFieldLabel,
} from '../components/LogoField';
import { RulesEditor } from '../components/RulesEditor';
import { useToast } from '../components/Toaster';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Input } from '../components/ui/input';
import { api, getToken, isProdApiEnv } from '../lib/api';
import { cn } from '../lib/cn';
import { domainOptions, useDomains } from '../lib/domains';

interface LinkDetail {
  id: string;
  name: string;
  billingUnitId: string | null;
  status: 'active' | 'paused';
  template: string;
  platform: string;
  domain: string;
  appName: string;
  logo: string;
  buttonColor: string;
  realUrl: string;
  decoyUrl: string;
  rulesOverrides: string;
  createdAt: number;
  updatedAt: number;
}

export const Route = createFileRoute('/link/$id')({
  beforeLoad: () => {
    if (!getToken()) throw redirect({ to: '/login' });
  },
  component: LinkDetailPage,
});

/** 统计一个 partial 覆盖里被覆盖的「原子项」数量（record 字段按键计数），供折叠头徽标展示。 */
function countOverrides(diff: Partial<RulesConfig>): number {
  let n = 0;
  for (const v of Object.values(diff)) {
    // record 字段(weights/enabled/clusterCaps)按键计数；标量/数组算 1 项。
    n += v && typeof v === 'object' && !Array.isArray(v) ? Object.keys(v).length : 1;
  }
  return n;
}

function LinkDetailPage() {
  const { id } = Route.useParams();
  const qc = useQueryClient();
  const toast = useToast();

  const { data } = useQuery({
    queryKey: ['link', id],
    queryFn: () => api<LinkDetail>(`/api/links/${id}`),
  });

  const platformRules = useQuery({
    queryKey: ['rules'],
    queryFn: () => api<RulesConfig>('/api/rules'),
  });

  const { data: domainsData } = useDomains();

  const [form, setForm] = useState<{
    name: string;
    billingUnitId: string | null;
    template: string;
    platform: string;
    domain: string;
    appName: string;
    logo: string;
    buttonColor: string;
    realUrl: string;
    decoyUrl: string;
  } | null>(null);
  // 链接级规则「有效配置」= 平台默认 merge 存储的 partial 覆盖。编辑器直接改这份具体值，
  // 保存时再 diff 回最小 partial 存储（见 diffRulesOverrides）。
  const [effective, setEffective] = useState<RulesConfig | null>(null);
  const [rulesOpen, setRulesOpen] = useState(false);

  const platform = platformRules.data;

  // 只在首次加载 / 切换到另一条链接时初始化表单与规则,按 id 用 ref 守卫。
  // 否则 React Query 的后台 refetch(窗口聚焦 / 断网恢复 / 缓存失效)会把管理员
  // 尚未保存的编辑静默重置。需要 platform 才能算 effective,故两者齐备才初始化。
  const initedFor = useRef<string | null>(null);
  const initialBillingUnit = useRef<string | null>(null);
  useEffect(() => {
    if (!data || !platform || initedFor.current === id) return;
    initedFor.current = id;
    initialBillingUnit.current = data.billingUnitId ?? null;
    setForm({
      name: data.name,
      billingUnitId: data.billingUnitId ?? null,
      template: data.template,
      platform: data.platform,
      domain: data.domain,
      appName: data.appName,
      logo: data.logo,
      buttonColor: data.buttonColor,
      realUrl: data.realUrl,
      decoyUrl: data.decoyUrl,
    });
    let overrides: Partial<RulesConfig> = {};
    try {
      overrides = JSON.parse(data.rulesOverrides);
    } catch {
      // ignore malformed stored overrides — fall back to platform defaults
    }
    setEffective(mergeRulesOverrides(platform, overrides));
  }, [data, platform, id]);

  const save = useMutation({
    mutationFn: (submittedBillingUnitId: string | null) =>
      api(`/api/links/${id}`, {
        method: 'PUT',
        body: JSON.stringify({
          name: form!.name,
          ...(submittedBillingUnitId !== initialBillingUnit.current
            ? {
                billingUnitId: submittedBillingUnitId,
                previousBillingUnitId: initialBillingUnit.current,
              }
            : {}),
          template: form!.template,
          platform: form!.platform,
          domain: form!.domain,
          appName: form!.appName,
          logo: form!.logo,
          buttonColor: form!.buttonColor,
          realUrl: form!.realUrl,
          decoyUrl: form!.decoyUrl,
          rulesOverrides: JSON.stringify(
            effective && platform ? diffRulesOverrides(effective, platform) : {},
          ),
        }),
      }),
    onSuccess: (_result, submittedBillingUnitId) => {
      initialBillingUnit.current = submittedBillingUnitId;
      qc.invalidateQueries({ queryKey: ['link', id] });
      qc.invalidateQueries({ queryKey: ['links'] });
      qc.invalidateQueries({ queryKey: ['billing-usage'] });
      qc.invalidateQueries({ queryKey: ['billing-units'] });
      qc.invalidateQueries({ queryKey: ['billing-links'] });
      qc.invalidateQueries({ queryKey: ['billing-link-options'] });
      toast.success('保存成功', '链接配置已更新');
    },
    onError: (e) => toast.error('保存失败', e instanceof Error ? e.message : '未知错误'),
  });

  if (!data || !form || !platform || !effective) {
    return <div className="p-6 text-sm text-zinc-500">加载中…</div>;
  }

  // 严格模式只在 Beta 平面投用,DC / RB 不跑这两条规则。
  const showStrictMode = !isProdApiEnv();
  const tpl = templateById(form.template);
  const defaultBtnColor = tpl?.defaultButtonColor ?? '#00BCD4';
  const overrideCount = countOverrides(diffRulesOverrides(effective, platform));

  const handleSave = () => {
    // Domain availability is only knowable once the list has loaded; saving
    // earlier would bypass the dead/suspended guard entirely.
    if (!domainsData) {
      toast.error('无法保存', '域名状态仍在加载中，请稍后重试');
      return;
    }
    const selectedDomain = domainOptions(domainsData).find(
      (option) => option.value === form.domain,
    );
    // A saved domain that no longer appears in the list is treated as
    // unavailable, same as an explicitly disabled one.
    if (!selectedDomain || selectedDomain.disabled) {
      toast.error('无法保存', '所选访问域名已失效、暂停或尚未激活，请更换可用域名');
      return;
    }
    save.mutate(form.billingUnitId);
  };

  return (
    <div className="mx-auto max-w-3xl">
      <RouterLink
        to="/links"
        className="mb-4 inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-700"
      >
        <ArrowLeft className="h-4 w-4" /> 返回链接列表
      </RouterLink>

      <div className="mb-6 flex items-center gap-3">
        <h1 className="text-xl font-semibold">/{data.id}</h1>
        <Badge variant={data.status === 'active' ? 'success' : 'info'}>
          {data.status === 'active' ? '运行中' : '已暂停'}
        </Badge>
      </div>

      <div className="space-y-4">
        <Card className="p-5">
          <h2 className="mb-3 text-sm font-semibold">基本信息</h2>
          <div className="space-y-3">
            <BillingUnitSelect
              value={form.billingUnitId}
              onChange={(billingUnitId) => setForm({ ...form, billingUnitId })}
            />
            <div>
              <label htmlFor="link-name" className="mb-1 block text-xs font-medium text-zinc-500">
                名称
              </label>
              <Input
                id="link-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div>
              <label
                htmlFor="link-template"
                className="mb-1 block text-xs font-medium text-zinc-500"
              >
                落地页模板
              </label>
              <select
                id="link-template"
                value={form.template}
                onChange={(e) => setForm({ ...form, template: e.target.value })}
                className="w-full rounded-md border border-zinc-200 bg-white px-2 py-1.5 text-sm dark:border-zinc-700 dark:bg-zinc-900"
              >
                {TEMPLATES.filter((tmpl) => !tmpl.disabled || tmpl.id === form.template).map(
                  (tmpl) => (
                    <option key={tmpl.id} value={tmpl.id}>
                      {tmpl.label}
                      {tmpl.disabled ? ' (已停用)' : ''}
                    </option>
                  ),
                )}
              </select>
            </div>
            <div>
              <label
                htmlFor="link-platform"
                className="mb-1 block text-xs font-medium text-zinc-500"
              >
                投放平台 (仅筛选用)
              </label>
              <select
                id="link-platform"
                value={form.platform}
                onChange={(e) => setForm({ ...form, platform: e.target.value })}
                className="w-full rounded-md border border-zinc-200 bg-white px-2 py-1.5 text-sm dark:border-zinc-700 dark:bg-zinc-900"
              >
                <option value="">未指定</option>
                {PLATFORMS.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.label}
                  </option>
                ))}
              </select>
            </div>
            {tpl?.hasAppName && (
              <>
                <div>
                  <label
                    htmlFor="link-appname"
                    className="mb-1 block text-xs font-medium text-zinc-500"
                  >
                    {appNameLabel(tpl)}
                  </label>
                  <Input
                    id="link-appname"
                    value={form.appName}
                    onChange={(e) => setForm({ ...form, appName: e.target.value })}
                    placeholder={appNamePlaceholder(tpl)}
                  />
                </div>
                <div>
                  <label
                    htmlFor="link-logo"
                    className="mb-1 block text-xs font-medium text-zinc-500"
                  >
                    {logoFieldLabel(tpl)}
                  </label>
                  <LogoField
                    tpl={tpl}
                    value={form.logo}
                    onChange={(logo) => setForm({ ...form, logo })}
                    inputId="link-logo"
                  />
                </div>
                {tpl?.hasButtonColor && (
                  <div>
                    <label
                      htmlFor="link-buttoncolor"
                      className="mb-1 block text-xs font-medium text-zinc-500"
                    >
                      按钮颜色 (留空默认 {defaultBtnColor})
                    </label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={form.buttonColor || defaultBtnColor}
                        onChange={(e) => setForm({ ...form, buttonColor: e.target.value })}
                        className="h-9 w-12 shrink-0 cursor-pointer rounded border border-zinc-200 p-0.5 dark:border-zinc-700"
                      />
                      <Input
                        id="link-buttoncolor"
                        value={form.buttonColor}
                        onChange={(e) => setForm({ ...form, buttonColor: e.target.value })}
                        placeholder={defaultBtnColor}
                      />
                    </div>
                  </div>
                )}
              </>
            )}
            <div>
              <label htmlFor="link-domain" className="mb-1 block text-xs font-medium text-zinc-500">
                访问域名
              </label>
              <select
                id="link-domain"
                value={form.domain}
                onChange={(e) => setForm({ ...form, domain: e.target.value })}
                className="w-full rounded-md border border-zinc-200 bg-white px-2 py-1.5 text-sm dark:border-zinc-700 dark:bg-zinc-900"
              >
                {domainOptions(domainsData).map((o) => (
                  <option key={o.value} value={o.value} disabled={o.disabled}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label
                htmlFor="link-realurl"
                className="mb-1 block text-xs font-medium text-zinc-500"
              >
                真实页 (通过后跳转)
              </label>
              <Input
                id="link-realurl"
                type="url"
                value={form.realUrl}
                onChange={(e) => setForm({ ...form, realUrl: e.target.value })}
              />
            </div>
            <div>
              <label
                htmlFor="link-decoyurl"
                className="mb-1 block text-xs font-medium text-zinc-500"
              >
                安全页 (拦截后跳转)
              </label>
              <Input
                id="link-decoyurl"
                type="url"
                value={form.decoyUrl}
                onChange={(e) => setForm({ ...form, decoyUrl: e.target.value })}
              />
            </div>
          </div>
        </Card>

        {showStrictMode && (
          <Card className="p-5">
            <h2 className="mb-3 text-sm font-semibold">严格模式</h2>
            <div className="flex items-start gap-3">
              <Checkbox
                id="link-strict-mode"
                className="mt-0.5"
                checked={effective.strictMode}
                // 关掉直接写 false 即可:与平台基线相同的标量会被 diffRulesOverrides
                // 丢出覆盖 JSON,不需要在这里手动删键。
                onCheckedChange={(v) => setEffective({ ...effective, strictMode: v === true })}
              />
              <label htmlFor="link-strict-mode" className="cursor-pointer text-sm">
                <span className="font-medium">开启严格模式</span>
                <span className="mt-1 block text-xs leading-relaxed text-zinc-500">
                  额外启用「安卓设备全程静止且零交互」与「同一设备反复到访同一链接」两条规则；
                  关闭后这两条规则完全不参与判定，链接恢复平台默认配置。
                </span>
              </label>
            </div>
          </Card>
        )}

        {/* override 平台规则 —— 复用平台规则编辑器，默认折叠。不改的项继承平台默认。 */}
        <Card className="overflow-hidden p-0">
          <div className="flex w-full items-center gap-2 px-5 py-4">
            <button
              type="button"
              onClick={() => setRulesOpen((s) => !s)}
              className="flex flex-1 items-center gap-2 text-left"
            >
              <ChevronRight
                className={cn(
                  'h-4 w-4 text-zinc-400 transition-transform',
                  rulesOpen && 'rotate-90',
                )}
              />
              <span className="text-sm font-semibold">override 平台规则</span>
              {overrideCount > 0 ? (
                <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 dark:bg-amber-500/15 dark:text-amber-400">
                  {overrideCount} 项已覆盖
                </Badge>
              ) : (
                <span className="text-xs text-zinc-400">全部继承平台默认</span>
              )}
            </button>
            {overrideCount > 0 && (
              <button
                type="button"
                onClick={() => setEffective(structuredClone(platform))}
                className="text-xs text-amber-600 hover:underline dark:text-amber-400"
              >
                全部恢复继承
              </button>
            )}
          </div>
          {rulesOpen && (
            <div className="border-t border-zinc-100 p-5 dark:border-zinc-800">
              <p className="mb-4 text-xs text-zinc-500">
                不设置的项目继承平台默认配置。改动的项会标记「已覆盖」，可单项或一键恢复继承。
              </p>
              <RulesEditor value={effective} onChange={setEffective} baseline={platform} />
            </div>
          )}
        </Card>

        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={save.isPending}>
            {save.isPending ? '保存中…' : '保存'}
          </Button>
        </div>
      </div>
    </div>
  );
}
